/**
 * Регрессионный тренд: прямая наименьших квадратов по закрытиям.
 *
 * Одна функция на всех: её читают и рисование канала, и выравнивание
 * опор после перетаскивания. Два разных расчёта дали бы канал в одном
 * месте, а опоры — в другом.
 */

import type { Candle } from '../state/store.js';
import type { DrawingPoint } from './types.js';

export interface RegressionFit {
  readonly first: Candle;
  readonly last: Candle;
  /** Цена линии в начале и в конце отрезка. */
  readonly start: number;
  readonly end: number;
  /** Стандартное отклонение закрытий от линии. */
  readonly deviation: number;
  /** Коэффициент корреляции Пирсона: −1…1, по модулю — сила тренда. */
  readonly r: number;
}

export function regressionFit(candles: readonly Candle[], from: number, to: number): RegressionFit | null {
  const [start, end] = from <= to ? [from, to] : [to, from];
  const inside = candles.filter((candle) => candle.time >= start && candle.time <= end);
  const n = inside.length;
  if (n < 3) return null;

  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  let sumYY = 0;
  inside.forEach((candle, index) => {
    sumX += index;
    sumY += candle.close;
    sumXY += index * candle.close;
    sumXX += index * index;
    sumYY += candle.close * candle.close;
  });
  const denominator = n * sumXX - sumX * sumX;
  if (denominator === 0) return null;
  const slope = (n * sumXY - sumX * sumY) / denominator;
  const intercept = (sumY - slope * sumX) / n;
  const deviation = Math.sqrt(
    inside.reduce((acc, candle, index) => acc + (candle.close - (intercept + slope * index)) ** 2, 0) / n,
  );
  const spreadY = n * sumYY - sumY * sumY;
  const r = spreadY <= 0 ? 0 : (n * sumXY - sumX * sumY) / Math.sqrt(denominator * spreadY);

  return { first: inside[0]!, last: inside[n - 1]!, start: intercept, end: intercept + slope * (n - 1), deviation, r };
}

/**
 * Опоры — на концы линии регрессии.
 *
 * Тянут опору как точку — куда отпустили, там она и лежит. Но цена
 * опоры в расчёте не участвует, и после отпускания её место — на самой
 * линии: оттуда следующее перетаскивание и начнётся.
 */
export function regressionPoints(points: readonly DrawingPoint[], candles: readonly Candle[]): DrawingPoint[] {
  const [a, b] = points;
  if (!a || !b) return [...points];
  const fit = regressionFit(candles, a.time, b.time);
  if (!fit) return [...points];
  const left = { time: fit.first.time, price: fit.start };
  const right = { time: fit.last.time, price: fit.end };
  return a.time <= b.time ? [left, right] : [right, left];
}

/** Сила тренда словами — по модулю коэффициента корреляции. */
export function trendWords(r: number): string {
  const strength = Math.abs(r);
  return strength >= 0.7 ? 'сильный тренд' : strength >= 0.4 ? 'умеренный тренд' : 'слабый тренд';
}
